import type { Category, CategoryCreate } from '@erp/shared';
import {
  Ban,
  ChevronDown,
  FolderTree,
  Image as ImageIcon,
  Pencil,
  RotateCcw,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, EmptyState, FormField } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import {
  deleteAttachment,
  fetchAttachmentObjectUrl,
  isImageMime,
  listAttachments,
  uploadAttachment,
} from '../../lib/attachments.js';
import { TranslationEditor } from '../cms/TranslationEditor.js';
import { ImageEditorDialog } from '../common/ImageEditorDialog.js';
import { categorySubtreeCounts } from '../items/itemHelpers.js';

interface CatRow {
  category: Category;
  depth: number;
  hasChildren: boolean;
}

function indexChildren(cats: Category[]): Map<string | null, Category[]> {
  const byParent = new Map<string | null, Category[]>();
  for (const c of cats) {
    const key = c.parentId ?? null;
    const list = byParent.get(key);
    if (list) list.push(c);
    else byParent.set(key, [c]);
  }
  // Siblings follow the shop's own running order (the Sort number), code as tiebreak.
  for (const list of byParent.values()) list.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.code.localeCompare(b.code));
  return byParent;
}

function subtreeMatches(c: Category, byParent: Map<string | null, Category[]>, q: string): boolean {
  if (c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)) return true;
  return (byParent.get(c.id) ?? []).some((k) => subtreeMatches(k, byParent, q));
}

/** Flatten the tree in (code-ordered) tree order, applying search + collapse. */
function flattenCat(
  byParent: Map<string | null, Category[]>,
  query: string,
  collapsed: ReadonlySet<string>,
): CatRow[] {
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const out: CatRow[] = [];
  const walk = (c: Category, depth: number): void => {
    if (searching && !subtreeMatches(c, byParent, q)) return;
    const kids = byParent.get(c.id) ?? [];
    out.push({ category: c, depth, hasChildren: kids.length > 0 });
    if (kids.length > 0 && !(collapsed.has(c.id) && !searching)) {
      for (const k of kids) walk(k, depth + 1);
    }
  };
  for (const root of byParent.get(null) ?? []) walk(root, 0);
  return out;
}

export function CategoriesPage(): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('items.write');
  const canFiles = usePermission('attachments.read');
  const [cats, setCats] = useState<Category[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const [query, setQuery] = useState('');
  const [photoVersion, setPhotoVersion] = useState(0);
  // Own (exact-category) item tally per category id; `null` until the first page
  // of the catalogue has been counted, so a loading row shows "—" not a false 0.
  const [itemCounts, setItemCounts] = useState<Map<string, number> | null>(null);

  const dataLocale = i18n.language.split('-')[0] ?? 'en';
  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: Category[] }>('/categories', { params: { locale: dataLocale } });
    setCats(r.data.items);
  };
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLocale]);
  useEffect(() => {
    // Item counts per category (client-side tally; the count links open the
    // items list filtered to that category). PAGE THROUGH the catalogue: one
    // bare /items call returns the server default of 200 rows, which read as
    // "0 items" on every category past the first couple of hundred.
    let active = true;
    void (async () => {
      const PAGE = 500;
      const m = new Map<string, number>();
      let seen = 0;
      for (let offset = 0; offset < 20_000; offset += PAGE) {
        try {
          const r = await api().get<{ items: Array<{ categoryId?: string | null }>; total?: number }>(
            '/items',
            { params: { limit: PAGE, offset } },
          );
          if (!active) return;
          for (const it of r.data.items) if (it.categoryId) m.set(it.categoryId, (m.get(it.categoryId) ?? 0) + 1);
          seen += r.data.items.length;
          setItemCounts(new Map(m)); // render each page as it lands
          if (r.data.items.length < PAGE || seen >= (r.data.total ?? seen)) break;
        } catch {
          if (active) setItemCounts(new Map(m)); // show what we managed to tally
          break;
        }
      }
    })();
    return () => { active = false; };
  }, []);

  const editing = editingId && cats ? cats.find((c) => c.id === editingId) ?? null : null;

  if (!cats) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const byParent = indexChildren(cats);
  const rows = flattenCat(byParent, query, collapsed);
  // Counts roll UP the tree: a parent reports its own items PLUS everything
  // filed under it, which is what the count link now opens. Totals across rows
  // will exceed the catalogue size — a child's items count for it and for every
  // ancestor, which is the point of a department count.
  const rollupCounts = categorySubtreeCounts(cats, itemCounts ?? new Map());
  const parentIds = cats.filter((c) => (byParent.get(c.id)?.length ?? 0) > 0).map((c) => c.id);
  const allCollapsed = parentIds.length > 0 && parentIds.every((id) => collapsed.has(id));

  const toggle = (c: Category): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(c.id)) next.delete(c.id);
      else next.add(c.id);
      return next;
    });
  const toggleAll = (): void => setCollapsed(allCollapsed ? new Set() : new Set(parentIds));

  const onDeactivate = async (c: Category): Promise<void> => {
    if (!(await confirmDialog({ message: t('categories.actions.confirmDeactivate', { code: c.code, defaultValue: `Deactivate ${c.code}?` }), tone: 'danger' }))) return;
    try {
      await api().delete(`/categories/${c.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };
  const onReactivate = async (c: Category): Promise<void> => {
    try {
      await api().patch(`/categories/${c.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  return (
    <div className="cat-view flex flex-col gap-4">
      <style>{CAT_CSS}</style>
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('categories.title', { defaultValue: 'Categories' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t('categories.description', { defaultValue: 'Organize items into a category tree. Each category can have a photo.' })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {cats.length > 0 && (
            <Button variant="secondary" onClick={toggleAll}>
              {allCollapsed
                ? t('categories.expandAll', { defaultValue: 'Expand all' })
                : t('categories.collapseAll', { defaultValue: 'Collapse all' })}
            </Button>
          )}
          {canWrite && !showCreate && !editingId && (
            <Button onClick={() => setShowCreate(true)}>
              {t('categories.create.title', { defaultValue: 'Add category' })}
            </Button>
          )}
        </div>
      </header>

      {showCreate && (
        <CategoryForm
          cats={cats}
          onCancel={() => setShowCreate(false)}
          onSaved={async () => {
            setShowCreate(false);
            setPhotoVersion((v) => v + 1);
            await reload();
          }}
        />
      )}
      {editing && (
        <CategoryForm
          cats={cats}
          initial={editing}
          onCancel={() => setEditingId(null)}
          onSaved={async () => {
            setEditingId(null);
            setPhotoVersion((v) => v + 1);
            await reload();
          }}
        />
      )}

      {cats.length === 0 && !showCreate ? (
        <EmptyState
          icon={<FolderTree className="h-8 w-8" aria-hidden />}
          title={t('categories.empty', { defaultValue: 'No categories yet' })}
          description={t('categories.emptyDescription', {
            defaultValue: 'Group your products into categories — nest them as deep as you need, and give each a photo.',
          })}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('categories.create.title', { defaultValue: 'Add category' })}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <label className="cat-search">
            <Search className="h-4 w-4 flex-none" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('categories.searchPlaceholder', { defaultValue: 'Search category or code…' })}
              aria-label={t('categories.searchPlaceholder', { defaultValue: 'Search category or code…' })}
            />
          </label>
          <Card className="overflow-hidden p-0">
            <div className="cat-scroll">
              <table className="cat">
                <thead>
                  <tr>
                    <th className="w-16" />
                    <th>{t('categories.columns.code', { defaultValue: 'Code' })}</th>
                    <th>{t('categories.columns.name', { defaultValue: 'Category' })}</th>
                    <th className="w-16">{t('categories.columns.sort', { defaultValue: 'Sort' })}</th>
                    <th className="w-24">{t('categories.columns.items', { defaultValue: 'Items' })}</th>
                    <th>{t('categories.columns.status', { defaultValue: 'Status' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ category: c, depth, hasChildren }) => {
                    const isCollapsed = collapsed.has(c.id);
                    return (
                      <tr key={c.id} className={`cat-row${hasChildren ? (depth === 0 ? ' row-group' : ' row-sub') : ''}`}>
                        <td>
                          <div className="cell">
                            <CategoryThumb category={c} version={photoVersion} enabled={canFiles} />
                          </div>
                        </td>
                        <td>
                          <div className="cell">
                            <span className="code">{c.code}</span>
                          </div>
                        </td>
                        <td>
                          <div className="cell" style={{ paddingInlineStart: `${depth * 20}px` }}>
                            <span className={`acc-name${hasChildren && isCollapsed ? ' collapsed' : ''}`}>
                              {hasChildren ? (
                                <button
                                  type="button"
                                  className="chev"
                                  onClick={() => toggle(c)}
                                  aria-expanded={!isCollapsed}
                                  aria-label={tc(isCollapsed ? 'actions.expand' : 'actions.collapse', { defaultValue: isCollapsed ? 'Expand' : 'Collapse' })}
                                >
                                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                                </button>
                              ) : (
                                <span className="chev chev-spacer" aria-hidden />
                              )}
                              <span className="nm">{c.name}</span>
                              {canWrite && (
                                <span className="cat-actions">
                                  <button type="button" onClick={() => { setShowCreate(false); setEditingId(c.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }} aria-label={`${tc('actions.edit')} ${c.code}`} title={tc('actions.edit')}>
                                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                                  </button>
                                  {c.isActive ? (
                                    <button type="button" onClick={() => void onDeactivate(c)} aria-label={`${t('categories.actions.deactivate', { defaultValue: 'Deactivate' })} ${c.code}`} title={t('categories.actions.deactivate', { defaultValue: 'Deactivate' })}>
                                      <Ban className="h-3.5 w-3.5" aria-hidden />
                                    </button>
                                  ) : (
                                    <button type="button" onClick={() => void onReactivate(c)} aria-label={`${t('categories.actions.reactivate', { defaultValue: 'Reactivate' })} ${c.code}`} title={t('categories.actions.reactivate', { defaultValue: 'Reactivate' })}>
                                      <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                                    </button>
                                  )}
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="cell">
                            <span className="font-mono tabular-nums text-fg-muted">{c.sort || ''}</span>
                          </div>
                        </td>
                        <td>
                          <div className="cell">
                            {(() => {
                              if (!itemCounts) return <span className="font-mono tabular-nums text-fg-subtle">—</span>;
                              const total = rollupCounts.get(c.id) ?? 0;
                              const own = itemCounts.get(c.id) ?? 0;
                              return total > 0 ? (
                                <Link
                                  to={`/inventory/items?category=${c.id}`}
                                  title={
                                    total > own
                                      ? t('categories.itemsInSubcategories', {
                                          n: total - own,
                                          defaultValue: 'Includes {{n}} in subcategories',
                                        })
                                      : undefined
                                  }
                                  className="font-mono font-semibold tabular-nums text-brand underline-offset-4 hover:underline"
                                >
                                  {total}
                                </Link>
                              ) : (
                                <span className="font-mono tabular-nums text-fg-subtle">0</span>
                              );
                            })()}
                          </div>
                        </td>
                        <td>
                          <div className="cell">
                            <span className={`status-pill ${c.isActive ? 'on' : 'off'}`}>
                              {t(`status.${c.isActive ? 'active' : 'inactive'}`, { defaultValue: c.isActive ? 'Active' : 'Inactive' })}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <div className="cat-empty">{t('categories.noMatches', { defaultValue: 'No categories match your search.' })}</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

/** 36px category photo thumbnail — first image attachment, folder fallback. */
function CategoryThumb({ category, version, enabled }: { category: Category; version: number; enabled: boolean }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let made: string | null = null;
    void (async () => {
      try {
        const list = await listAttachments('category', category.id);
        const img = list.filter((a) => isImageMime(a.mimeType)).sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt))[0];
        if (!img) { if (active) setUrl(null); return; }
        const u = await fetchAttachmentObjectUrl(img.id);
        if (active) { made = u; setUrl(u); } else URL.revokeObjectURL(u);
      } catch { if (active) setUrl(null); }
    })();
    return () => { active = false; if (made) URL.revokeObjectURL(made); };
  }, [category.id, version, enabled]);
  return (
    <div className="grid h-9 w-9 flex-none place-items-center overflow-hidden rounded-lg border border-border bg-bg-subtle">
      {url ? <img src={url} alt="" className="h-full w-full object-contain" /> : <FolderTree className="h-[18px] w-[18px] text-fg-muted" aria-hidden />}
    </div>
  );
}

interface CategoryFormProps {
  cats: Category[];
  initial?: Category;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function CategoryForm({ cats, initial, onCancel, onSaved }: CategoryFormProps): JSX.Element {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const isEdit = !!initial;
  const canFiles = usePermission('attachments.write');
  const [form, setForm] = useState<CategoryCreate>(() =>
    initial
      ? { code: initial.code, name: initial.name, description: initial.description ?? undefined, parentId: initial.parentId ?? undefined, sort: initial.sort ?? 0 }
      : { code: '', name: '' },
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Tree rows are shown in the active language; load the BASE (default-language)
  // values for editing so saving the Name field can't overwrite the default
  // with a translation. Other languages are managed by the TranslationEditor.
  useEffect(() => {
    if (!isEdit || !initial) return;
    let active = true;
    void api().get<Category>(`/categories/${initial.id}`).then((r) => {
      if (active) setForm({ code: r.data.code, name: r.data.name, description: r.data.description ?? undefined, parentId: r.data.parentId ?? undefined, sort: r.data.sort ?? 0 });
    }).catch(() => undefined);
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initial?.id]);

  // Photo (single image attachment, entityType='category').
  const imgInputRef = useRef<HTMLInputElement>(null);
  const objUrlRef = useRef<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [stagedBlob, setStagedBlob] = useState<Blob | null>(null);
  const [existingImageId, setExistingImageId] = useState<string | null>(null);
  const [removeExisting, setRemoveExisting] = useState(false);
  const [editorFile, setEditorFile] = useState<File | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const showImage = useCallback((next: string | null): void => {
    if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
    objUrlRef.current = next;
    setImageUrl(next);
  }, []);

  useEffect(() => {
    if (!isEdit || !initial || !canFiles) return undefined;
    let active = true;
    void (async () => {
      try {
        const list = await listAttachments('category', initial.id);
        const img = list.filter((a) => isImageMime(a.mimeType)).sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt))[0];
        if (!img || !active) return;
        setExistingImageId(img.id);
        const u = await fetchAttachmentObjectUrl(img.id);
        if (active) showImage(u); else URL.revokeObjectURL(u);
      } catch { /* no photo */ }
    })();
    return () => { active = false; };
  }, [isEdit, initial, canFiles, showImage]);

  useEffect(() => () => { if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current); }, []);

  // Parent options: all categories except self (backend guards deeper cycles).
  const parentOptions = (() => {
    const byParent = indexChildren(cats);
    const out: { id: string; label: string }[] = [];
    const walk = (c: Category, depth: number): void => {
      if (c.id !== initial?.id) out.push({ id: c.id, label: `${'— '.repeat(depth)}${c.name}` });
      for (const k of byParent.get(c.id) ?? []) walk(k, depth + 1);
    };
    for (const r of byParent.get(null) ?? []) walk(r, 0);
    return out;
  })();

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    let catId: string | undefined;
    try {
      if (isEdit && initial) {
        await api().patch(`/categories/${initial.id}`, {
          name: form.name.trim(),
          description: form.description?.trim() || null,
          parentId: form.parentId ?? null,
          sort: Number.isInteger(Number(form.sort)) ? Math.max(0, Number(form.sort)) : 0,
        });
        catId = initial.id;
      } else {
        const payload: CategoryCreate = { code: form.code.trim(), name: form.name.trim() };
        if (form.description?.trim()) payload.description = form.description.trim();
        if (form.parentId) payload.parentId = form.parentId;
        if (form.sort !== undefined) payload.sort = Number.isInteger(Number(form.sort)) ? Math.max(0, Number(form.sort)) : 0;
        const created = await api().post<Category>('/categories', payload);
        catId = created.data.id;
      }
      if (catId && canFiles && (stagedBlob || removeExisting)) {
        try {
          if (existingImageId && (removeExisting || stagedBlob)) await deleteAttachment(existingImageId);
          if (stagedBlob) await uploadAttachment('category', catId, new File([stagedBlob], 'category-image.jpg', { type: 'image/jpeg' }));
        } catch { /* photo non-blocking */ }
      }
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      if (code === 'conflict') setError(t('errors.duplicateCode', { defaultValue: 'That code is already in use.' }));
      else if (code === 'unprocessable') setError(t('categories.errors.cycle', { defaultValue: 'A category can’t be its own parent or descendant.' }));
      else setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {isEdit ? t('categories.edit.title', { name: initial?.name, defaultValue: `Edit ${initial?.name}` }) : t('categories.create.title', { defaultValue: 'Add category' })}
      </h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        {canFiles && (
          <div className="md:col-span-2 flex items-center gap-4">
            <div className="grid h-20 w-20 flex-none place-items-center overflow-hidden rounded-xl border border-border bg-bg-subtle">
              {imageUrl ? <img src={imageUrl} alt="" className="h-full w-full object-contain" /> : <ImageIcon className="h-7 w-7 text-fg-muted" aria-hidden />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-fg">{t('categories.create.photo', { defaultValue: 'Category photo' })}</div>
              <p className="mt-0.5 text-xs text-fg-muted">{t('categories.create.photoHint', { defaultValue: 'Crop & rotate — compressed automatically.' })}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <input ref={imgInputRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) { setEditorFile(f); setEditorOpen(true); } if (e.target) e.target.value = ''; }} />
                <Button type="button" variant="secondary" size="sm" onClick={() => imgInputRef.current?.click()}>
                  <Upload className="h-4 w-4" aria-hidden />
                  {imageUrl ? t('categories.create.changePhoto', { defaultValue: 'Change' }) : t('categories.create.uploadPhoto', { defaultValue: 'Upload photo' })}
                </Button>
                {imageUrl && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setStagedBlob(null); showImage(null); if (existingImageId) setRemoveExisting(true); }}>
                    <Trash2 className="h-4 w-4" aria-hidden />
                    {t('categories.create.removePhoto', { defaultValue: 'Remove' })}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        <FormField id="cat-code" label={t('categories.create.code', { defaultValue: 'Code' })} required>
          <Input id="cat-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required autoFocus={!isEdit} readOnly={isEdit} aria-readonly={isEdit} className={isEdit ? 'cursor-not-allowed bg-bg-muted text-fg-muted' : undefined} />
        </FormField>
        <FormField id="cat-name" label={t('categories.create.name', { defaultValue: 'Name' })} required>
          <Input id="cat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus={isEdit} />
        </FormField>
        <FormField id="cat-parent" label={t('categories.create.parent', { defaultValue: 'Parent category' })}>
          <select id="cat-parent" className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg" value={form.parentId ?? ''} onChange={(e) => setForm({ ...form, parentId: e.target.value || undefined })}>
            <option value="">{t('categories.create.parentNone', { defaultValue: '— None (top level) —' })}</option>
            {parentOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </FormField>
        <FormField id="cat-desc" label={t('categories.create.description', { defaultValue: 'Description' })}>
          <Input id="cat-desc" value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </FormField>
        <FormField
          id="cat-sort"
          label={t('categories.create.sort', { defaultValue: 'Sort order' })}
          helperText={t('categories.create.sortHint', { defaultValue: 'Lower shows first — everywhere categories are listed.' })}
        >
          <Input id="cat-sort" type="number" min={0} value={String(form.sort ?? 0)} onChange={(e) => setForm({ ...form, sort: Number(e.target.value) })} />
        </FormField>
        {isEdit && initial && (
          <div className="md:col-span-2">
            <TranslationEditor
              entityType="category"
              entityId={initial.id}
              fields={[
                { key: 'name', label: t('categories.create.name', { defaultValue: 'Name' }) },
                { key: 'description', label: t('categories.create.description', { defaultValue: 'Description' }) },
              ]}
            />
          </div>
        )}
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{isEdit ? tc('actions.save') : t('categories.create.submit', { defaultValue: 'Create category' })}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>

      <ImageEditorDialog
        open={editorOpen}
        source={editorFile}
        title={t('categories.create.editPhoto', { defaultValue: 'Edit category photo' })}
        onCancel={() => { setEditorOpen(false); setEditorFile(null); }}
        onSave={(blob) => { setStagedBlob(blob); setRemoveExisting(false); showImage(URL.createObjectURL(blob)); setEditorOpen(false); setEditorFile(null); }}
      />
    </Card>
  );
}

const CAT_CSS = `
.cat-view .cat-search { display: inline-flex; align-items: center; gap: 8px; height: 36px; width: min(320px, 100%); padding: 0 12px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-md); color: var(--text-subtle); }
.cat-view .cat-search input { border: 0; background: transparent; outline: none; color: var(--text); font: inherit; font-size: var(--fs-sm); flex: 1; min-width: 0; }
.cat-view .cat-scroll { max-height: calc(var(--vh100) - 18rem); overflow: auto; }
.cat-view table.cat { width: 100%; border-collapse: collapse; }
.cat-view .cat thead th { position: sticky; top: 0; z-index: 2; background: var(--surface); text-align: start; font-size: var(--fs-over); font-weight: 700; letter-spacing: var(--tracking-over); text-transform: uppercase; color: var(--text-subtle); padding: 11px var(--s-4); border-bottom: 1px solid var(--border); white-space: nowrap; }
[dir='rtl'] .cat-view .cat thead th { letter-spacing: 0; }
.cat-view .cat td { padding: 0; border-bottom: 1px solid var(--border-soft); font-size: var(--fs-sm); color: var(--text); }
.cat-view .cat td .cell { padding: 8px var(--s-4); }
.cat-view .cat .code { font-family: var(--font-num); color: var(--text-subtle); font-weight: 600; }
.cat-view .acc-name { display: flex; align-items: center; gap: 8px; }
.cat-view .acc-name .chev { width: 18px; height: 18px; flex: none; display: grid; place-items: center; color: var(--text-subtle); transition: transform .15s, background .12s, color .12s; cursor: pointer; border: 0; background: transparent; padding: 0; border-radius: 5px; }
.cat-view .acc-name .chev:hover { color: var(--text); background: var(--surface-2); }
.cat-view .acc-name .chev-spacer { visibility: hidden; cursor: default; }
.cat-view .acc-name.collapsed .chev { transform: rotate(-90deg); }
.cat-view .acc-name.collapsed .chev:dir(rtl) { transform: rotate(90deg); }
.cat-view .acc-name .nm { font-weight: 600; }
.cat-view .row-group .cell { background: var(--surface-2); }
.cat-view .row-group .nm { font-weight: 800; }
.cat-view .row-sub .nm { font-weight: 700; }
.cat-view tr.cat-row:hover .cell { background: color-mix(in oklab, var(--primary) 6%, var(--surface)); }
.cat-view .row-group:hover .cell { background: var(--surface-2); }
.cat-view .status-pill { display: inline-flex; align-items: center; height: 20px; padding: 0 8px; border-radius: var(--r-pill); font-size: 11px; font-weight: 700; }
.cat-view .status-pill.on { background: color-mix(in oklab, var(--success) 16%, transparent); color: var(--success); }
.cat-view .status-pill.off { background: var(--surface-2); color: var(--text-subtle); }
.cat-view .cat-actions { opacity: 0; display: inline-flex; gap: 2px; margin-inline-start: 6px; transition: opacity .12s; }
.cat-view tr.cat-row:hover .cat-actions, .cat-view .acc-name:focus-within .cat-actions { opacity: 1; }
.cat-view .cat-actions button { width: 26px; height: 26px; border-radius: 6px; display: grid; place-items: center; color: var(--text-subtle); border: 0; background: transparent; cursor: pointer; }
.cat-view .cat-actions button:hover { background: var(--surface-2); color: var(--text); }
.cat-view .cat-empty { padding: 40px 16px; text-align: center; color: var(--text-muted); font-size: var(--fs-sm); }
`;
